// =============================================================================
// FILE: packages/renderer/src/lib/submittalGenerator.ts
// PURPOSE: "One-Click Submittal Set" — a professional, AEC-grade multi-page PDF
//   engineering submittal, assembled client-side. Clean white sheets, thin-line
//   borders, a standardized drawing TITLE BLOCK (project/drawing no., discipline,
//   revision history, designed/checked/approved, scale, status, sheet no.) and a
//   minimal RCD² professional stamp on every sheet.
//     Page 1 — Cover / index    Page 2 — System overview    Page 3 — Rack elevation
//   Three document styles: corporate | rcd2-branded | client-standard.
// =============================================================================

import { jsPDF } from 'jspdf';
import type { DashboardStats } from '../hooks/useDashboard.js';

export const PAGE_W = 1754;   // A4 landscape @ ~150 DPI
export const PAGE_H = 1240;

export type DocumentStyle = 'corporate' | 'rcd2-branded' | 'client-standard';
export type SheetStatus = 'Preliminary' | 'Design Development' | 'Construction Issue' | 'Record Drawing';

export interface Revision { rev: string; date: string; description: string; by: string }

export interface SubmittalMeta {
  author:       string;
  productName:  string;
  tierLabel:    string;
  tierColor:    string;
  projectName:  string;
  // Professional title-block fields (all optional — sensible defaults applied)
  projectNumber?: string;
  drawingTitle?:  string;
  drawingNumber?: string;
  discipline?:    string;
  scale?:         string;
  status?:        SheetStatus;
  designedBy?:    string;
  checkedBy?:     string;
  approvedBy?:    string;
  revisions?:     Revision[];
  documentStyle?: DocumentStyle;
}

export interface SubmittalResult {
  blobUrl: string; fileName: string; firstPageDataUrl: string; pageDataUrls: string[];
}

export interface Palette { paper: string; ink: string; sub: string; line: string; accent: string; accent2: string; logo: number; band: boolean; note: string }

export function palette(style: DocumentStyle): Palette {
  switch (style) {
    case 'rcd2-branded':
      return { paper: '#ffffff', ink: '#111827', sub: '#6b7280', line: '#c3ccd8', accent: '#0d9488', accent2: '#b8860b', logo: 1, band: true, note: 'RCD² Engine · BIM OPS Platform' };
    case 'client-standard':
      return { paper: '#ffffff', ink: '#1f2937', sub: '#6b7280', line: '#cbd5e1', accent: '#475569', accent2: '#64748b', logo: 0.4, band: false, note: 'Prepared for consultant / client review' };
    default: // corporate
      return { paper: '#ffffff', ink: '#1f2937', sub: '#64748b', line: '#c7ced8', accent: '#0f766e', accent2: '#334155', logo: 0.6, band: false, note: 'Professional engineering submittal' };
  }
}

export function esc(s: string): string { return (s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); }

/** Compact RCD² monogram, dark ink on white — subtle, professional. */
export function logoMark(x: number, y: number, scale: number, accent: string): string {
  return `<g transform="translate(${x},${y}) scale(${scale})">
    <rect x="0" y="0" width="9" height="52" rx="4" fill="#111827" />
    <path d="M 11,3 C 42,3 42,30 11,30" fill="none" stroke="${accent}" stroke-width="8" stroke-linecap="round" />
    <path d="M 13,32 Q 26,35 44,51" fill="none" stroke="${accent}" stroke-width="7" stroke-linecap="round" opacity="0.9" />
    <text x="27" y="14" font-family="Georgia, serif" font-weight="800" font-size="26" fill="#111827">2</text>
  </g>`;
}

function withDefaults(m: SubmittalMeta): Required<Omit<SubmittalMeta, 'tierColor' | 'documentStyle'>> & Pick<SubmittalMeta, 'tierColor' | 'documentStyle'> {
  return {
    author: m.author, productName: m.productName, tierLabel: m.tierLabel, tierColor: m.tierColor,
    projectName: m.projectName,
    projectNumber: m.projectNumber || 'RCD2-—',
    drawingTitle: m.drawingTitle || 'Telecommunications Infrastructure — Submittal Set',
    drawingNumber: m.drawingNumber || 'T-001',
    discipline: m.discipline || 'Telecommunications (ICT)',
    scale: m.scale || 'NTS',
    status: m.status || 'Design Development',
    designedBy: m.designedBy || m.author,
    checkedBy: m.checkedBy || '—',
    approvedBy: m.approvedBy || '—',
    revisions: (m.revisions && m.revisions.length) ? m.revisions : [{ rev: '0', date: new Date().toLocaleDateString('en-CA'), description: 'Issued for review', by: (m.author.split(' ').map((w) => w[0]).join('') || 'R2') }],
    documentStyle: m.documentStyle,
  };
}

// ─── Professional title block (bottom-right) + RCD² stamp (to its left) ──────────
function titleBlock(m: SubmittalMeta, dateStr: string, sheetNo: string, pageNo: number, pageTotal: number, pal: Palette): string {
  const d = withDefaults(m);
  const bw = 660, bh = 292;
  const bx = PAGE_W - 60 - bw, by = PAGE_H - 60 - bh;
  const T = (x: number, y: number, s: string, size: number, weight = 400, fill = pal.ink, ls = 0, anchor = 'start') =>
    `<text x="${x}" y="${y}" font-family="Arial, sans-serif" font-size="${size}" font-weight="${weight}" fill="${fill}" letter-spacing="${ls}" text-anchor="${anchor}">${esc(s)}</text>`;
  const L = (x1: number, y1: number, x2: number, y2: number) => `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="${pal.line}" stroke-width="1" />`;

  // Revision history (up to 4 rows), newest first.
  const revs = [...d.revisions].slice(-4).reverse();
  const revRows = revs.map((r, i) => {
    const ry = by + 150 + i * 26;
    return `${T(bx + 12, ry + 17, r.rev, 13, 700)}${T(bx + 52, ry + 17, r.date, 12)}${T(bx + 190, ry + 17, r.description, 12)}${T(bx + bw - 14, ry + 17, r.by, 12, 400, pal.ink, 0, 'end')}${L(bx, ry + 26, bx + bw, ry + 26)}`;
  }).join('');

  // Status highlight (chosen one bold accent)
  const statuses: SheetStatus[] = ['Preliminary', 'Design Development', 'Construction Issue', 'Record Drawing'];
  const statusChips = statuses.map((s, i) => {
    const chosen = s === d.status;
    return T(bx + 12 + (i % 2) * 320, by + bh - 44 + Math.floor(i / 2) * 20, `${chosen ? '■' : '□'} ${s}`, 12, chosen ? 700 : 400, chosen ? pal.accent : pal.sub);
  }).join('');

  return `
    <!-- Title block frame -->
    <rect x="${bx}" y="${by}" width="${bw}" height="${bh}" fill="#ffffff" stroke="${pal.ink}" stroke-width="1.5" />
    ${logoMark(bx + 14, by + 12, pal.logo, pal.accent)}
    ${T(bx + 14 + 60 * pal.logo + 10, by + 30, 'RCD² ENGINE', 18, 800, pal.ink, 1)}
    ${T(bx + 14 + 60 * pal.logo + 10, by + 50, 'BIM OPS Platform · Telecommunications Infrastructure Design', 11, 400, pal.sub, 0.5)}
    ${L(bx, by + 66, bx + bw, by + 66)}

    <!-- Project / drawing identity -->
    ${T(bx + 12, by + 88, 'PROJECT', 9, 700, pal.sub, 1)}
    ${T(bx + 12, by + 108, d.projectName, 15, 700)}
    ${T(bx + bw - 14, by + 88, 'PROJECT No.', 9, 700, pal.sub, 1, 'end')}
    ${T(bx + bw - 14, by + 108, d.projectNumber, 14, 700, pal.ink, 0, 'end')}
    ${L(bx, by + 118, bx + bw, by + 118)}
    ${T(bx + 12, by + 138, 'DRAWING TITLE', 9, 700, pal.sub, 1)}
    ${T(bx + 12, by + 138 + 0, '', 9)}
    ${T(bx + 230, by + 138, d.drawingTitle, 13, 600)}
    <line x1="${bx + 218}" y1="${by + 118}" x2="${bx + 218}" y2="${by + bh}" stroke="${pal.line}" stroke-width="1" />

    <!-- Revision history header -->
    ${T(bx + 12, by + 138, 'REV', 9, 700, pal.sub, 1)}

    <!-- (Revision table lives in left column under identity) -->
    ${T(bx + 12, by + 145, 'REVISION HISTORY', 8.5, 700, pal.sub, 1)}
    ${revRows}

    <!-- Right column: discipline / scale / date / sheet + roles -->
    ${T(bx + 230, by + 162, 'DISCIPLINE', 9, 700, pal.sub, 1)}${T(bx + bw - 14, by + 162, d.discipline, 12, 600, pal.ink, 0, 'end')}
    ${L(bx + 218, by + 172, bx + bw, by + 172)}
    ${T(bx + 230, by + 194, 'DESIGNED', 9, 700, pal.sub, 1)}${T(bx + bw - 14, by + 194, d.designedBy, 12, 600, pal.ink, 0, 'end')}
    ${T(bx + 230, by + 216, 'CHECKED', 9, 700, pal.sub, 1)}${T(bx + bw - 14, by + 216, d.checkedBy, 12, 600, pal.ink, 0, 'end')}
    ${T(bx + 230, by + 238, 'APPROVED', 9, 700, pal.sub, 1)}${T(bx + bw - 14, by + 238, d.approvedBy, 12, 600, pal.ink, 0, 'end')}
    ${L(bx + 218, by + 248, bx + bw, by + 248)}
    ${T(bx + 230, by + 268, 'DATE', 9, 700, pal.sub, 1)}${T(bx + 300, by + 268, dateStr, 12, 600)}
    ${T(bx + 470, by + 268, 'SCALE', 9, 700, pal.sub, 1)}${T(bx + 528, by + 268, d.scale, 12, 600)}
    ${T(bx + bw - 14, by + 268, `DWG ${d.drawingNumber}  ·  SHEET ${sheetNo}  ·  ${pageNo}/${pageTotal}`, 11, 700, pal.accent, 0, 'end')}

    <!-- Status row -->
    ${statusChips}
  `;
}

export function pageFrame(inner: string, m: SubmittalMeta, dateStr: string, sheetNo: string, pageNo: number, pageTotal: number, pal: Palette): string {
  const band = pal.band
    ? `<rect x="46" y="46" width="${PAGE_W - 92}" height="10" fill="${pal.accent}" /><rect x="46" y="56" width="${PAGE_W - 92}" height="4" fill="${pal.accent2}" />`
    : '';
  return `
  <svg xmlns="http://www.w3.org/2000/svg" width="${PAGE_W}" height="${PAGE_H}" viewBox="0 0 ${PAGE_W} ${PAGE_H}">
    <rect width="${PAGE_W}" height="${PAGE_H}" fill="${pal.paper}" />
    <rect x="34" y="34" width="${PAGE_W - 68}" height="${PAGE_H - 68}" fill="none" stroke="${pal.ink}" stroke-width="2" />
    <rect x="46" y="46" width="${PAGE_W - 92}" height="${PAGE_H - 92}" fill="none" stroke="${pal.line}" stroke-width="1" />
    ${band}
    ${inner}
    ${titleBlock(m, dateStr, sheetNo, pageNo, pageTotal, pal)}
    <text x="70" y="${PAGE_H - 44}" font-family="Arial, sans-serif" font-size="11" fill="${pal.sub}" letter-spacing="0.5">${esc(pal.note)}  ·  rcd2engine.com</text>
  </svg>`;
}

// ─── Page 1 — Cover / index ───────────────────────────────────────────────────
function coverSvg(m: SubmittalMeta, dateStr: string, pal: Palette): string {
  const d = withDefaults(m);
  const inner = `
    ${logoMark(90, 110, 1.6 * pal.logo + 0.5, pal.accent)}
    <text x="90" y="300" font-family="Georgia, serif" font-weight="800" font-size="70" fill="${pal.ink}">${esc(d.productName)}</text>
    <text x="94" y="352" font-family="Arial, sans-serif" font-weight="600" font-size="26" fill="${pal.accent}" letter-spacing="6">ENGINEERING SUBMITTAL SET</text>
    <line x1="90" y1="392" x2="${PAGE_W - 760}" y2="392" stroke="${pal.line}" stroke-width="1.5" />
    <text x="90" y="470" font-family="Arial, sans-serif" font-size="15" fill="${pal.sub}" letter-spacing="2">PROJECT</text>
    <text x="90" y="510" font-family="Arial, sans-serif" font-weight="700" font-size="34" fill="${pal.ink}">${esc(d.projectName)}</text>
    <text x="90" y="576" font-family="Arial, sans-serif" font-size="15" fill="${pal.sub}" letter-spacing="2">DRAWING TITLE</text>
    <text x="90" y="612" font-family="Arial, sans-serif" font-weight="600" font-size="24" fill="${pal.ink}">${esc(d.drawingTitle)}</text>
    <g font-family="Arial, sans-serif">
      ${[['Project No.', d.projectNumber], ['Discipline', d.discipline], ['Status', d.status], ['Date', dateStr]].map(([k, v], i) => `
        <text x="90" y="${690 + i * 40}" font-size="15" fill="${pal.sub}">${esc(String(k))}</text>
        <text x="360" y="${690 + i * 40}" font-size="16" font-weight="700" fill="${pal.ink}">${esc(String(v))}</text>`).join('')}
    </g>
    <g font-family="Arial, sans-serif">
      <text x="${PAGE_W - 720}" y="150" font-size="13" fill="${pal.sub}" letter-spacing="2">SHEET INDEX</text>
      ${[['T-001', 'Cover / Submittal Index'], ['T-101', 'System Overview Diagram'], ['T-201', 'Rack Elevation — MDF-01']].map(([n, t], i) => `
        <text x="${PAGE_W - 720}" y="${196 + i * 36}" font-size="15" font-weight="700" fill="${pal.accent}">${esc(String(n))}</text>
        <text x="${PAGE_W - 610}" y="${196 + i * 36}" font-size="15" fill="${pal.ink}">${esc(String(t))}</text>`).join('')}
    </g>`;
  return pageFrame(inner, m, dateStr, 'T-001', 1, 3, pal);
}

// ─── Page 2 — System overview ─────────────────────────────────────────────────
function overviewSvg(s: DashboardStats | undefined, m: SubmittalMeta, dateStr: string, pal: Palette): string {
  const d = s ?? ({} as DashboardStats);
  const nodes = [
    { label: 'Projects', count: d.projects ?? 0, x: 110, y: 430 },
    { label: 'Buildings', count: d.buildings ?? 0, x: 430, y: 430 },
    { label: 'Telecom Spaces', count: d.telecomSpaces ?? 0, x: 750, y: 300 },
    { label: 'Racks', count: d.racks ?? 0, x: 750, y: 560 },
    { label: 'Devices', count: d.assets ?? 0, x: 1070, y: 430 },
    { label: 'Connections', count: d.connections ?? 0, x: 1150, y: 720 },
  ];
  const NW = 250, NH = 120;
  const edges: Array<[number, number]> = [[0, 1], [1, 2], [1, 3], [2, 4], [3, 4], [4, 5]];
  const edgeSvg = edges.map(([a, b]) => {
    const s1 = nodes[a]!, t1 = nodes[b]!;
    const x1 = s1.x + NW, y1 = s1.y + NH / 2, x2 = t1.x, y2 = t1.y + NH / 2, mx = (x1 + x2) / 2;
    return `<path d="M ${x1},${y1} C ${mx},${y1} ${mx},${y2} ${x2},${y2}" fill="none" stroke="${pal.line}" stroke-width="1.5" />`;
  }).join('');
  const nodeSvg = nodes.map((n) => `
    <g transform="translate(${n.x},${n.y})">
      <rect x="0" y="0" width="${NW}" height="${NH}" fill="#ffffff" stroke="${pal.ink}" stroke-width="1.25" />
      <rect x="0" y="0" width="6" height="${NH}" fill="${pal.accent}" />
      <text x="26" y="44" font-family="Arial, sans-serif" font-size="18" font-weight="700" letter-spacing="1" fill="${pal.ink}">${esc(n.label.toUpperCase())}</text>
      <text x="26" y="96" font-family="Georgia, serif" font-size="46" font-weight="800" fill="${pal.accent}">${n.count.toLocaleString()}</text>
    </g>`).join('');
  const inner = `
    <text x="90" y="150" font-family="Arial, sans-serif" font-size="15" fill="${pal.sub}" letter-spacing="4">SYSTEM TOPOLOGY</text>
    <text x="90" y="200" font-family="Georgia, serif" font-weight="800" font-size="46" fill="${pal.ink}">System Overview Diagram</text>
    <text x="90" y="236" font-family="Arial, sans-serif" font-size="16" fill="${pal.sub}">Live project topology sourced from the offline-first persistence layer.</text>
    ${edgeSvg}${nodeSvg}`;
  return pageFrame(inner, m, dateStr, 'T-101', 2, 3, pal);
}

// ─── Page 3 — Rack elevation ────────────────────────────────────────────────────
function rackSvg(s: DashboardStats | undefined, m: SubmittalMeta, dateStr: string, pal: Palette): string {
  const d = s ?? ({} as DashboardStats);
  const U = 42, rackX = 560, rackY = 190, rackW = 460, uH = 18, rackH = U * uH;
  const items: Array<{ u: number; h: number; label: string }> = [];
  let cursor = 1;
  const push = (h: number, label: string) => { if (cursor + h - 1 > U) return; items.push({ u: cursor, h, label }); cursor += h; };
  const switches = Math.max(1, Math.min(6, d.switches ?? 2));
  const panels = Math.max(1, Math.min(6, d.patchPanels ?? 2));
  push(2, 'FIBER ENTRANCE / ODF'); push(1, 'CABLE MANAGER');
  for (let i = 0; i < panels; i++) { push(1, `PATCH PANEL 48-PORT · P${i + 1}`); push(1, 'CABLE MANAGER'); }
  for (let i = 0; i < switches; i++) push(1, `CORE SWITCH · SW${i + 1}`);
  push(1, 'CABLE MANAGER'); push(2, 'UPS / POWER DISTRIBUTION'); push(4, 'SERVER · APPLICATION HOST');
  const rows = Array.from({ length: U }, (_, i) => {
    const y = rackY + i * uH;
    return `<line x1="${rackX}" y1="${y}" x2="${rackX + rackW}" y2="${y}" stroke="${pal.line}" stroke-width="0.6" />
      <text x="${rackX - 12}" y="${y + uH - 5}" text-anchor="end" font-family="monospace" font-size="11" fill="${pal.sub}">${U - i}</text>`;
  }).join('');
  const eqSvg = items.map((it) => {
    const y = rackY + (U - (it.u + it.h - 1)) * uH, h = it.h * uH;
    return `<rect x="${rackX + 5}" y="${y + 2}" width="${rackW - 10}" height="${h - 4}" fill="#f8fafc" stroke="${pal.ink}" stroke-width="1" />
      <rect x="${rackX + 5}" y="${y + 2}" width="5" height="${h - 4}" fill="${pal.accent}" />
      <text x="${rackX + 22}" y="${y + h / 2 + 5}" font-family="Arial, sans-serif" font-size="14" font-weight="600" fill="${pal.ink}">${esc(it.label)}</text>`;
  }).join('');
  const inner = `
    <text x="90" y="150" font-family="Arial, sans-serif" font-size="15" fill="${pal.sub}" letter-spacing="4">DIGITAL TWIN</text>
    <text x="90" y="200" font-family="Georgia, serif" font-weight="800" font-size="46" fill="${pal.ink}">Rack Elevation — MDF-01</text>
    <text x="90" y="236" font-family="Arial, sans-serif" font-size="16" fill="${pal.sub}">Primary MDF · 42U · front elevation.</text>
    <g font-family="Arial, sans-serif">
      <text x="90" y="320" font-size="15" fill="${pal.sub}" letter-spacing="1">RACK SUMMARY</text>
      ${[['Total Racks', d.racks ?? 0], ['Switches', d.switches ?? 0], ['Patch Panels', d.patchPanels ?? 0], ['Ports', d.ports ?? 0], ['Connections', d.connections ?? 0], ['Fiber Cables', d.fiberCables ?? 0]].map(([k, v], i) => `
        <text x="90" y="${372 + i * 52}" font-size="16" fill="${pal.ink}">${esc(String(k))}</text>
        <text x="420" y="${372 + i * 52}" font-size="20" font-weight="800" fill="${pal.accent}" text-anchor="end">${(v as number).toLocaleString()}</text>
        <line x1="90" y1="${384 + i * 52}" x2="420" y2="${384 + i * 52}" stroke="${pal.line}" stroke-width="0.6" />`).join('')}
    </g>
    <rect x="${rackX - 34}" y="${rackY - 34}" width="${rackW + 68}" height="${rackH + 74}" fill="none" stroke="${pal.ink}" stroke-width="1.25" />
    <text x="${rackX + rackW / 2}" y="${rackY - 12}" text-anchor="middle" font-family="Arial, sans-serif" font-size="16" font-weight="700" letter-spacing="2" fill="${pal.ink}">MDF-01 · 42U</text>
    ${rows}${eqSvg}`;
  return pageFrame(inner, m, dateStr, 'T-201', 3, 3, pal);
}

// ─── Rasterization (white paper) ─────────────────────────────────────────────────
export function rasterize(svg: string, paper: string, scale = 1): Promise<string> {
  return new Promise((resolve, reject) => {
    const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const img = new Image();
    const w = Math.round(PAGE_W * scale), h = Math.round(PAGE_H * scale);
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext('2d')!;
      ctx.fillStyle = paper; ctx.fillRect(0, 0, w, h);
      ctx.drawImage(img, 0, 0, w, h);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/png', scale < 1 ? 0.7 : 0.92));
    };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

// ─── Deliverable protection: preview watermark + licensed fingerprint ─────────────
export interface WatermarkInfo { name: string; projectId: string; timestamp: string; }

/** Personalized diagonal PREVIEW watermark + identity footer band. Overlaid on top
 *  of a preview page's content so removing it removes identifying information. */
export function watermarkLayer(info: WatermarkInfo, pal: Palette): string {
  const tiles: string[] = [];
  for (let y = 120; y < PAGE_H; y += 210) {
    for (let x = -60; x < PAGE_W; x += 520) {
      tiles.push(`<text x="${x}" y="${y}" font-family="Arial, sans-serif" font-size="34" font-weight="800" fill="${pal.accent}" opacity="0.10" transform="rotate(-24 ${x} ${y})" letter-spacing="4">R2 PREVIEW · NOT LICENSED</text>`);
    }
  }
  const bandY = PAGE_H - 132;
  return `<g pointer-events="none">
    ${tiles.join('')}
    <rect x="0" y="${bandY}" width="${PAGE_W}" height="34" fill="#b91c1c" opacity="0.92" />
    <text x="26" y="${bandY + 23}" font-family="Arial, sans-serif" font-size="16" font-weight="800" fill="#ffffff" letter-spacing="1">R2 PREVIEW ONLY — NOT LICENSED FOR CONSTRUCTION USE</text>
    <text x="${PAGE_W - 26}" y="${bandY + 23}" text-anchor="end" font-family="Arial, sans-serif" font-size="13" fill="#ffffff">Generated for ${esc(info.name)} · Project ${esc(info.projectId)} · ${esc(info.timestamp)}</text>
  </g>`;
}

/** Licensed-file authentication footer carrying the server-issued fingerprint. */
export function fingerprintFooter(code: string, licenseId: string, pal: Palette): string {
  return `<g pointer-events="none">
    <text x="26" y="${PAGE_H - 20}" font-family="monospace" font-size="12" fill="${pal.sub}">R2 Document Authentication: ${esc(code)}  ·  License ${esc(licenseId)}</text>
  </g>`;
}

export async function generateSubmittalSet(
  stats: DashboardStats | undefined,
  meta: SubmittalMeta,
  onProgress?: (dataUrl: string, pageIndex: number) => void,
): Promise<SubmittalResult> {
  const pal = palette(meta.documentStyle ?? 'corporate');
  const dateStr = new Date().toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' });
  const svgs = [coverSvg(meta, dateStr, pal), overviewSvg(stats, meta, dateStr, pal), rackSvg(stats, meta, dateStr, pal)];
  const pageDataUrls: string[] = [];
  for (let i = 0; i < svgs.length; i++) {
    const dataUrl = await rasterize(svgs[i]!, pal.paper);
    pageDataUrls.push(dataUrl);
    onProgress?.(dataUrl, i);
  }
  const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const pw = pdf.internal.pageSize.getWidth(), ph = pdf.internal.pageSize.getHeight();
  pageDataUrls.forEach((url, i) => { if (i > 0) pdf.addPage(); pdf.addImage(url, 'PNG', 0, 0, pw, ph, undefined, 'FAST'); });
  const fileName = `RCD2-Submittal-${meta.projectName.replace(/[^a-z0-9]+/gi, '-')}-${Date.now()}.pdf`;
  const blobUrl = URL.createObjectURL(pdf.output('blob'));
  return { blobUrl, fileName, firstPageDataUrl: pageDataUrls[0]!, pageDataUrls };
}
